import { Tooltip } from '@mantine/core';
import { Check, X } from 'lucide-react';
import classes from './SessionAttendanceBox.module.css';

export interface SessionAttendanceEntry {
  session_number: number;
  status: 'attended' | 'present' | 'partial' | 'absent' | 'excused' | 'pending' | null;
}

const STATUS_CLASS_MAP: Record<string, string> = {
  attended: classes.sessionDotAttended,
  present: classes.sessionDotAttended,
  partial: classes.sessionDotPartial,
  absent: classes.sessionDotAbsent,
  excused: classes.sessionDotExcused,
  pending: classes.sessionDotPending,
};

const ICON_MAP: Record<string, (sessionNumber: number) => React.ReactNode> = {
  attended: () => <Check size={12} />,
  present: () => <Check size={12} />,
  partial: () => <span>!</span>,
  absent: () => <X size={12} />,
  excused: () => <span>E</span>,
  pending: (n) => <span>{n}</span>,
};

/** Tooltip-friendly status label */
function getStatusLabel(status: string | null): string {
  if (!status) return 'N/A';
  if (status === 'present') return 'attended';
  return status;
}

/**
 * Shared per-session attendance box.
 * Mirrors the SessionDot from AttendanceHealthDrillDown but is importable
 * from any Data Library tab.
 */
export function SessionAttendanceBox({ entry }: { entry: SessionAttendanceEntry }) {
  const { status, session_number } = entry;
  const dotClass = status ? (STATUS_CLASS_MAP[status] ?? classes.sessionDotNull) : classes.sessionDotNull;
  const icon = status
    ? (ICON_MAP[status]?.(session_number) ?? <span>{session_number}</span>)
    : <span>{session_number}</span>;

  const tooltip = `Session ${session_number}: ${getStatusLabel(status)}`;

  return (
    <Tooltip label={tooltip} withArrow>
      <div className={`${classes.sessionDot} ${dotClass}`} tabIndex={0} role="img" aria-label={tooltip}>
        {icon}
      </div>
    </Tooltip>
  );
}

/**
 * Row of per-session attendance boxes for use in table cells.
 */
export function SessionAttendanceBoxList({ entries }: { entries: SessionAttendanceEntry[] }) {
  if (!entries || entries.length === 0) {
    return null;
  }

  return (
    <div className={classes.sessionIndicators}>
      {entries.map(entry => (
        <SessionAttendanceBox key={entry.session_number} entry={entry} />
      ))}
    </div>
  );
}
